import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Search, ShoppingBag, User } from 'lucide-react';
export const Navigation = () => {
  const links = ['THE MASK', 'BUILD', 'ARSENAL', 'BUNDLES', 'INTEL', 'CONTACT'];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [cartUrl, setCartUrl] = useState<string | null>(null);
  const lastScrollY = useRef(0);
  useEffect(() => {
    const handlePriceUpdate = (e: any) => {
      const {
        qty,
        activeBundle,
        totalAddons,
        cartUrl
      } = e.detail;
      const maskCount = activeBundle ? activeBundle.count : qty;
      setCartCount(maskCount + totalAddons);
      setCartUrl(cartUrl);
    };
    window.addEventListener('priceUpdate', handlePriceUpdate);
    return () => window.removeEventListener('priceUpdate', handlePriceUpdate);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      // Always show at the very top
      if (y < 80) {
        setVisible(true);
      } else if (y > lastScrollY.current + 5) {
        // Scrolling down → hide
        setVisible(false);
        if (open) setOpen(false);
      } else if (y < lastScrollY.current - 5) {
        // Scrolling up → show
        setVisible(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, [open]);
  return <AnimatePresence data-id="element-1149">
      {visible && <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} exit={{
      y: -100
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className={`fixed top-[36px] w-full z-40 transition-[height,background,border,box-shadow] duration-300 ${scrolled ? 'h-[68px] bg-base-black/95 border-b border-acid-green/15 shadow-[0_4px_30px_rgba(0,0,0,0.6)]' : 'h-[84px] bg-base-black/80 border-b border-acid-green/5'} backdrop-blur-[20px] flex items-center justify-between px-4 md:px-10`} data-id="element-1150">
          {/* Logo */}
          <a href="#" className="flex items-center group shrink-0" data-id="element-1151">
            <img src="https://cdn.magicpatterns.com/uploads/gtew43sgjbWAUi8V7My12k/hf_20260505_154603_f5f185ee-8480-4693-8c47-91f27540bf0d.png" alt="Blackout Unit" className={`w-auto object-contain transition-all duration-300 group-hover:scale-105 ${scrolled ? 'h-12' : 'h-14 md:h-16'}`} data-id="element-1152" />
          </a>

          {/* Desktop Links - centered */}
          <div className="hidden lg:flex items-center gap-10 font-barlow font-bold text-[11px] tracking-[3px] text-text-body absolute left-1/2 -translate-x-1/2" data-id="element-1153">
            {links.map(link => <a key={link} href={`#${link.toLowerCase().replace(' ', '-')}`} className="hover:text-white transition-colors relative group py-2" data-id="element-1154">
                {link}
                <span className="absolute -bottom-0.5 left-0 w-0 h-[1px] bg-acid-green group-hover:w-full transition-all duration-300" data-id="element-1155"></span>
              </a>)}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1 sm:gap-2" data-id="element-1156">
            <button aria-label="Search" className="hidden sm:flex w-10 h-10 items-center justify-center text-text-body hover:text-acid-green transition-colors" data-id="element-1157">
              <Search size={18} strokeWidth={1.5} data-id="element-1158" />
            </button>

            <button aria-label="Account" className="hidden sm:flex w-10 h-10 items-center justify-center text-text-body hover:text-acid-green transition-colors" data-id="element-1159">
              <User size={18} strokeWidth={1.5} data-id="element-1160" />
            </button>

            <button aria-label="Cart" onClick={() => {
          if (cartUrl) {
            window.location.href = cartUrl;
          } else {
            document.getElementById('the-mask')?.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }} className="relative w-10 h-10 flex items-center justify-center text-text-body hover:text-acid-green transition-colors group" data-id="element-1161">
              <ShoppingBag size={20} strokeWidth={1.5} data-id="element-1162" />
              {cartCount > 0 && <motion.span key={cartCount} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-acid-green text-black font-barlow font-bold text-[10px] tracking-tighter rounded-full flex items-center justify-center group-hover:bg-white transition-colors" data-id="element-1163">
                  {cartCount}
                </motion.span>}
            </button>

            <span className="hidden sm:block w-px h-6 bg-acid-green/15 mx-2" data-id="element-1164"></span>

            <button onClick={() => document.getElementById('the-mask')?.scrollIntoView({
          behavior: 'smooth'
        })} className="hidden sm:block bg-acid-green text-black font-barlow font-bold text-[11px] tracking-[3px] px-5 py-3 clip-diagonal hover:bg-white transition-colors" data-id="element-1165">
              DEPLOY NOW
            </button>

            <button onClick={() => setOpen(!open)} aria-label="Toggle menu" className="lg:hidden w-10 h-10 flex items-center justify-center text-acid-green ml-1" data-id="element-1166">
              {open ? <X size={22} data-id="element-1167" /> : <Menu size={22} data-id="element-1168" />}
            </button>
          </div>

          {/* Mobile Drawer */}
          {open && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:hidden absolute top-full left-0 w-full bg-[#050505] border-b border-acid-green/15 py-4 px-6 flex flex-col shadow-2xl" data-id="element-1169">
              {links.map(link => <a key={link} href={`#${link.toLowerCase().replace(' ', '-')}`} onClick={() => setOpen(false)} className="font-barlow font-bold text-sm tracking-[3px] text-text-body hover:text-acid-green transition-colors py-3 border-b border-dark-3" data-id="element-1170">
                  {link}
                </a>)}
              <div className="flex items-center gap-3 mt-5" data-id="element-1171">
                <button className="flex-1 flex items-center justify-center gap-2 border border-dark-3 text-text-body py-3 font-barlow font-bold text-xs tracking-[3px]" data-id="element-1172">
                  <Search size={14} data-id="element-1173" /> SEARCH
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 border border-dark-3 text-text-body py-3 font-barlow font-bold text-xs tracking-[3px]" data-id="element-1174">
                  <User size={14} data-id="element-1175" /> ACCOUNT
                </button>
              </div>
              <button onClick={() => {
          setOpen(false);
          document.getElementById('the-mask')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="mt-4 bg-acid-green text-black font-barlow font-bold text-xs tracking-[3px] py-4 clip-diagonal w-full" data-id="element-1176">
                DEPLOY NOW
              </button>
            </motion.div>}
        </motion.nav>}
    </AnimatePresence>;
};